/**
 * Reliable extension messaging utility.
 * Wraps chrome.runtime.sendMessage with timeout and retry logic.
 */

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function sendOnce(
  extId: string,
  message: Record<string, any>,
  timeout: number
): Promise<any> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error("Extension message timeout"));
    }, timeout);

    try {
      chrome.runtime.sendMessage(extId, message, (response) => {
        clearTimeout(timer);
        if (chrome.runtime.lastError) {
          reject(new Error(chrome.runtime.lastError.message));
        } else {
          resolve(response);
        }
      });
    } catch (err) {
      clearTimeout(timer);
      reject(err);
    }
  });
}

/**
 * Sends a message to the Haven extension with timeout and retry.
 *
 * @param extId - Extension ID to send to
 * @param message - Message payload
 * @param options.timeout - Per-attempt timeout in ms (default: 2000)
 * @param options.retries - Number of retries after first failure (default: 1, so 2 total attempts)
 * @returns The extension's response, or undefined if all attempts fail
 */
export async function sendExtensionMessage(
  extId: string,
  message: Record<string, any>,
  options?: { timeout?: number; retries?: number }
): Promise<any> {
  if (typeof chrome === "undefined" || !chrome.runtime?.sendMessage) {
    return undefined;
  }

  const timeout = options?.timeout ?? 2000;
  const retries = options?.retries ?? 1;
  const totalAttempts = 1 + retries;

  for (let attempt = 0; attempt < totalAttempts; attempt++) {
    try {
      return await sendOnce(extId, message, timeout);
    } catch {
      if (attempt < totalAttempts - 1) {
        await delay(500);
      }
    }
  }

  // All attempts exhausted — return undefined (best-effort)
  return undefined;
}
